import http from 'hub-http/clients/apiClient';
import { FREQUENCY } from 'web-interactives-dashboard-ui/constants/AnalyticsConstants';
import { lruMemoizeAsyncExceptInTests } from '../utils/memoize';
import { formatDate } from 'web-interactives-dashboard-ui/utils/dateUtils';
import getEarlyRequesterResultPromise from './getEarlyRequesterResultPromise';
import { mergeByInteractive, mergePointSummaries, mergePoints } from '../utils/analyticsUtils';
const BASE_URL = 'web-interactives/v1/analyze';
const cleanRequest = object => {
  return Object.keys(object).reduce((result, key) => {
    if (object[key]) {
      return Object.assign({
        [key]: object[key]
      }, result);
    }
    return result;
  }, {});
};
const convertToAttributedSubmissionsCounters = counters => {
  return {
    attributedFormSubmissions: counters.formSubmissions,
    attributedFormSubmissionsRate: counters.formSubmissionsRate
  };
};
const convertToAttributedSubmissionsCountersContainerList = list => {
  for (const container of list) {
    container.counters = convertToAttributedSubmissionsCounters(container.counters);
  }
  return list;
};
const convertToAttributedSubmissionsResponse = response => {
  if (response.byInteractiveTotals.results) {
    response.byInteractiveTotals.results = convertToAttributedSubmissionsCountersContainerList(response.byInteractiveTotals.results);
  }
  if (response.pointsSummaryResult.results) {
    response.pointsSummaryResult.results = convertToAttributedSubmissionsCountersContainerList(response.pointsSummaryResult.results);
  }
  response.totals.currentCounters = convertToAttributedSubmissionsCounters(response.totals.currentCounters);
  response.totals.prevCounters = convertToAttributedSubmissionsCounters(response.totals.prevCounters);
  return response;
};
const ENDPOINT_GROUP_BY = {
  ctas: 'ID',
  submissions: 'CALL_TO_ACTION_ID',
  'attributed-submissions': 'ATTRIBUTED_CALL_TO_ACTION_ID'
};
const ENDPOINT_EARLY_REQUESTER_DETAILS_NAME_PREFIX = {
  ctas: 'details-data',
  submissions: 'details-submissions-data',
  'attributed-submissions': 'details-attributed-submissions-data'
};
const ENDPOINT_EARLY_REQUESTER_ANALYZE_NAME = {
  ctas: 'analyze-data',
  submissions: 'analyze-submissions-data',
  'attributed-submissions': 'analyze-attributed-submissions-data'
};
const fetchAllForFrequency = lruMemoizeAsyncExceptInTests((endpoint, start, end, {
  campaignId,
  containerType,
  interactiveId
}, frequency = FREQUENCY.WEEKLY) => {
  return http.post(`${BASE_URL}/${endpoint}/all`, {
    data: cleanRequest({
      contentId: interactiveId,
      campaignId,
      containerType,
      startDateInt: start,
      endDateInt: end,
      groupBy: ENDPOINT_GROUP_BY[endpoint],
      frequency
    }),
    timeout: 30000
  });
});
const fetchInitialAllForFrequency = lruMemoizeAsyncExceptInTests((endpoint, start, end, filterParams, frequency = FREQUENCY.WEEKLY) => {
  const {
    interactiveId
  } = filterParams;
  const earlyRequestPromise = getEarlyRequesterResultPromise(interactiveId ? `${ENDPOINT_EARLY_REQUESTER_DETAILS_NAME_PREFIX[endpoint]}-${interactiveId}` : ENDPOINT_EARLY_REQUESTER_ANALYZE_NAME[endpoint]);
  return earlyRequestPromise || fetchAllForFrequency(endpoint, start, end, filterParams, frequency);
});
const getFetchPointsForFrequency = lruMemoizeAsyncExceptInTests((endpoint, {
  campaignId,
  containerType,
  startDate,
  endDate,
  interactiveId
}, frequency = FREQUENCY.WEEKLY) => http.post(`${BASE_URL}/${endpoint}/points`, {
  data: cleanRequest({
    contentId: interactiveId,
    campaignId,
    containerType,
    startDateInt: formatDate(startDate),
    endDateInt: formatDate(endDate),
    groupBy: ENDPOINT_GROUP_BY[endpoint],
    frequency
  }),
  timeout: 30000
}));
export const fetchPointsForFrequency = lruMemoizeAsyncExceptInTests((filterParams, frequency = FREQUENCY.WEEKLY) => Promise.all([getFetchPointsForFrequency('ctas', filterParams, frequency), getFetchPointsForFrequency('submissions', filterParams, frequency), getFetchPointsForFrequency('attributed-submissions', filterParams, frequency)]).then(([clicks, submissions, attributedSubmissions]) => ({
  startDateInt: clicks.startDateInt,
  endDateInt: clicks.endDateInt,
  results: mergePointSummaries(clicks.results, submissions.results, attributedSubmissions.results && convertToAttributedSubmissionsCountersContainerList(attributedSubmissions.results))
})));
const combineFormSubmissionData = (all, formSubmissionsData, attributedFormSubmissionsData) => {
  const result = Object.assign({}, all);
  result.byInteractiveTotals.results = mergeByInteractive(result.byInteractiveTotals.results, formSubmissionsData === null || formSubmissionsData === void 0 ? void 0 : formSubmissionsData.byInteractiveTotals.results, attributedFormSubmissionsData === null || attributedFormSubmissionsData === void 0 ? void 0 : attributedFormSubmissionsData.byInteractiveTotals.results);
  result.pointsSummaryResult.results = mergePointSummaries(result.pointsSummaryResult.results, formSubmissionsData === null || formSubmissionsData === void 0 ? void 0 : formSubmissionsData.pointsSummaryResult.results, attributedFormSubmissionsData === null || attributedFormSubmissionsData === void 0 ? void 0 : attributedFormSubmissionsData.pointsSummaryResult.results);
  result.totals.currentCounters = mergePoints(result.totals.currentCounters, formSubmissionsData === null || formSubmissionsData === void 0 ? void 0 : formSubmissionsData.totals.currentCounters, attributedFormSubmissionsData === null || attributedFormSubmissionsData === void 0 ? void 0 : attributedFormSubmissionsData.totals.currentCounters);
  result.totals.prevCounters = mergePoints(result.totals.prevCounters, formSubmissionsData === null || formSubmissionsData === void 0 ? void 0 : formSubmissionsData.totals.prevCounters, attributedFormSubmissionsData === null || attributedFormSubmissionsData === void 0 ? void 0 : attributedFormSubmissionsData.totals.prevCounters);
  return result;
};
export const fetchDataForAnalyze = (() => {
  let isInitial = true;
  return lruMemoizeAsyncExceptInTests((filterParams, frequency) => {
    const formattedStartDate = formatDate(filterParams.startDate);
    const formattedEndDate = formatDate(filterParams.endDate);
    const opts = [formattedStartDate, formattedEndDate, filterParams, frequency];
    if (isInitial) {
      isInitial = false;
      return Promise.all([fetchInitialAllForFrequency('ctas', ...opts), fetchInitialAllForFrequency('submissions', ...opts), fetchInitialAllForFrequency('attributed-submissions', ...opts)]).then(([all, formSubmissionData, attributedFormSubmissionsData]) => ({
        analyzeResponse: combineFormSubmissionData(all, formSubmissionData, convertToAttributedSubmissionsResponse(attributedFormSubmissionsData))
      }));
    }
    return Promise.all([fetchAllForFrequency('ctas', ...opts), fetchAllForFrequency('submissions', ...opts), fetchAllForFrequency('attributed-submissions', ...opts)]).then(([all, formSubmissionData, attributedFormSubmissionsData]) => ({
      analyzeResponse: combineFormSubmissionData(all, formSubmissionData, convertToAttributedSubmissionsResponse(attributedFormSubmissionsData))
    }));
  });
})();